import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { GET_ALL_PACKAGES } from "../../queries";
import type { IPackage, TPackage } from "../../types";
import { getEPSSNumber, getPackageStatus } from "../../utils";
import type {
  GetPackagesQueryVariables,
  ToePackagesSortInput,
} from "gql/graphql";
import { useAudit } from "hooks/use-audit";

interface IGetPackagesResponse {
  fetchMore: () => Promise<void>;
  hasNextPage: boolean;
  loading: boolean;
  packages: TPackage[];
  total: number;
}

const useGetPackages = (
  groupName: string,
  filters: GetPackagesQueryVariables["filters"],
  sortBy: ToePackagesSortInput[],
): IGetPackagesResponse => {
  const { addAuditEvent } = useAudit();
  const {
    data,
    fetchMore: fetchMorePackages,
    loading,
  } = useQuery(GET_ALL_PACKAGES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    onCompleted: (): void => {
      addAuditEvent("Group.ToePackages", groupName);
    },
    variables: {
      filters,
      first: 101,
      groupName,
      sortBy,
    },
  });

  const pageInfo = data?.group.toePackages.pageInfo ?? {
    endCursor: "",
    hasNextPage: false,
  };

  const packages = useMemo((): IPackage[] => {
    const edges = data?.group.toePackages.edges ?? [];

    return edges.map((edge): IPackage => {
      const epss = getEPSSNumber(edge.node);

      const status = getPackageStatus(edge.node);

      return {
        ...edge.node,
        epss,
        status,
      };
    });
  }, [data]);

  return {
    fetchMore: async (): Promise<void> => {
      await fetchMorePackages({ variables: { after: pageInfo.endCursor } });
    },
    hasNextPage: pageInfo.hasNextPage,
    loading,
    packages,
    total: data?.group.toePackages.total ?? 0,
  };
};

export { useGetPackages };
